import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { generateServiceSchema, generateOrganizationSchema, generateFAQSchema, generateBreadcrumbSchema, injectSchema } from '@/lib/swissSchema';
import { updateMetaTags } from '@/utils/seoCore';
import { generateHreflangLinks, updateHreflangLinks } from '@/lib/swissHreflang';
import { SwissLanguage, getCityDisplayName } from '@/lib/swissI18n';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, CheckCircle, TrendingUp, Globe, Target, Clock, BarChart3 } from 'lucide-react';

const SEOService = () => {
  const { city } = useParams();
  const { currentLanguage } = useLanguage();
  const language = currentLanguage as SwissLanguage;
  
  const cityDisplayName = city ? getCityDisplayName(language, city) : null;
  const isHubPage = !city;
  
  const content = {
    de: {
      title: isHubPage 
        ? 'SEO & Suchmaschinenoptimierung – Technisches & Multilingual SEO'
        : `SEO Agentur ${cityDisplayName} – Technisches & Multilingual SEO`,
      description: isHubPage
        ? 'Professionelle SEO-Beratung für die Schweiz. Technisches SEO, Multilingual (de-CH, fr-CH, it-CH), lokale Suchmaschinenoptimierung. Messbare Ergebnisse in 90 Tagen.'
        : `SEO Agentur ${cityDisplayName}: de-CH, fr-CH, it-CH Optimierung. Technisches SEO, Content, Backlinks. Sichtbar in der ganzen Schweiz. Strategiegespräch buchen.`,
      h1: isHubPage
        ? 'SEO & Suchmaschinenoptimierung'
        : `SEO Agentur ${cityDisplayName}`,
      subtitle: isHubPage
        ? 'Technisches & Multilingual SEO für die Schweiz'
        : `Sichtbar in der ganzen Schweiz – SEO aus ${cityDisplayName}`,
      hero: 'Mehr Sichtbarkeit, mehr Kunden, mehr Umsatz. Unser technisches und multilinguales SEO bringt Sie bei Google auf Seite 1 – in der Deutschschweiz, Romandie und Tessin.',
      
      packages: [
        {
          name: 'SEO Audit',
          price: 'CHF 1\'500',
          features: [
            'Vollständige Website-Analyse',
            'Technisches SEO Audit',
            'Keyword-Recherche',
            'Wettbewerbs-Analyse',
            'Actionable Roadmap',
            '90-Tage Prioritätenliste'
          ]
        },
        {
          name: 'SEO Optimierung',
          price: 'CHF 2\'900/Monat',
          features: [
            'Technisches SEO',
            'Content-Optimierung',
            'Lokales SEO Setup',
            'Multilingual hreflang',
            'Monatliches Reporting',
            'Keyword Tracking',
            '6 Monate Laufzeit'
          ],
          popular: true
        },
        {
          name: 'SEO Domination',
          price: 'CHF 4\'500/Monat',
          features: [
            'Full-Service SEO',
            'Content Creation',
            'Link Building',
            'Technical Maintenance',
            'Multilingual Expansion',
            'Conversion Optimierung',
            'Weekly Calls'
          ]
        }
      ],
      
      process: [
        { step: 1, title: 'SEO Audit & Analyse', desc: 'Vollständige Analyse der aktuellen Performance' },
        { step: 2, title: 'Keyword-Strategie', desc: 'Lokale und multilingual Keywords identifizieren' },
        { step: 3, title: 'Technische Optimierung', desc: 'Core Web Vitals, hreflang, Schema Markup' },
        { step: 4, title: 'Content & Link Building', desc: 'Hochwertige Inhalte und Authority Building' },
        { step: 5, title: 'Monitoring & Scaling', desc: 'Kontinuierliche Optimierung und Expansion' }
      ],
      
      specialties: [
        { icon: Globe, title: 'Multilingual SEO', desc: 'de-CH, fr-CH, it-CH mit korrekten hreflang Tags' },
        { icon: Target, title: 'Lokales SEO', desc: 'Google Business Profile und lokale Sichtbarkeit' },
        { icon: BarChart3, title: 'Technisches SEO', desc: 'Core Web Vitals, Schema, Page Speed' },
        { icon: TrendingUp, title: 'Performance SEO', desc: 'Conversion-fokussierte Optimierung' }
      ],
      
      faq: [
        {
          question: `Was kostet SEO in ${cityDisplayName || 'der Schweiz'}?`,
          answer: 'SEO Audit ab CHF 1\'500. Laufende Optimierung ab CHF 2\'900/Monat. Enterprise SEO ab CHF 4\'500/Monat. ROI-orientierte Preisgestaltung.'
        },
        {
          question: 'Wie lange dauert es, bis SEO Ergebnisse sichtbar sind?',
          answer: 'Erste Verbesserungen nach 30-60 Tagen. Signifikante Rankings nach 3-6 Monaten. Volle Wirkung nach 6-12 Monaten bei konsequenter Umsetzung.'
        },
        {
          question: 'Was ist Multilingual SEO für die Schweiz?',
          answer: 'Optimierung für de-CH, fr-CH, it-CH mit korrekten hreflang Tags, lokalen Keywords und kulturell angepassten Inhalten für alle Sprachregionen.'
        },
        {
          question: 'Bieten Sie lokales SEO für Swiss Cities?',
          answer: 'Ja. Google Business Profile Optimierung, lokale Citations, Stadt-spezifische Landing Pages für Zürich, Basel, Bern, Genf, Lausanne.'
        },
        {
          question: 'Wie messen Sie SEO Erfolg?',
          answer: 'Rankings, organischer Traffic, Conversion Rate, lokale Sichtbarkeit. Monatliche Reports mit ROI-Analyse und Handlungsempfehlungen.'
        },
        {
          question: 'Was ist Core Web Vitals Optimierung?',
          answer: 'Page Speed, Largest Contentful Paint (LCP), Interaction to Next Paint (INP), Cumulative Layout Shift (CLS). Google Ranking-Faktoren seit 2021.'
        }
      ]
    }
  };

  const currentContent = content[language] || content.de;
  
  useEffect(() => {
    updateMetaTags({
      title: currentContent.title,
      description: currentContent.description,
      canonicalUrl: `https://www.future-level.ch/${language}/seo${city ? `/${city}` : ''}`,
      language: language,
      keywords: `SEO, Suchmaschinenoptimierung, Multilingual SEO, ${cityDisplayName || 'Schweiz'}, hreflang`
    });

    const hreflangLinks = generateHreflangLinks({
      currentLanguage: language,
      currentPath: `/seo${city ? `/${city}` : ''}`,
      availableLanguages: ['de', 'fr', 'it', 'en']
    });
    updateHreflangLinks(hreflangLinks);

    const orgSchema = generateOrganizationSchema({ language });
    const serviceSchema = generateServiceSchema({
      serviceName: 'SEO & Suchmaschinenoptimierung',
      serviceSlug: 'seo',
      city,
      language,
      priceRange: 'CHF 1500-4500',
      description: currentContent.description,
      url: `https://www.future-level.ch/${language}/seo${city ? `/${city}` : ''}`
    });
    const breadcrumbSchema = generateBreadcrumbSchema(language, 'seo', city);
    const faqSchema = generateFAQSchema({ 
      questions: currentContent.faq, 
      language 
    });

    injectSchema({
      "@context": "https://schema.org",
      "@graph": [orgSchema, serviceSchema, breadcrumbSchema, faqSchema]
    });
  }, [language, city, cityDisplayName]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-green-950 to-slate-900">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-green-600/20 text-green-200 border-green-500/30">
                <Search className="w-4 h-4 mr-2" />
                SEO Spezialist {cityDisplayName && `• ${cityDisplayName}`}
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {currentContent.h1}
                <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent block">
                  {currentContent.subtitle}
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                {currentContent.hero}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white">
                  <Search className="w-5 h-5 mr-2" />
                  Kostenloses SEO-Audit
                </Button>
                <Button size="lg" variant="outline" className="border-green-500/30 text-green-200 hover:bg-green-900/20">
                  Fallstudien ansehen
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Specialties Section */}
        <section className="py-20 bg-black/20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Unsere SEO-Spezialgebiete
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {currentContent.specialties.map((specialty, index) => (
                <Card key={index} className="bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-slate-700 text-center">
                  <CardContent className="p-6">
                    <specialty.icon className="w-12 h-12 text-green-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">{specialty.title}</h3>
                    <p className="text-gray-300 text-sm">{specialty.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Packages Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                SEO Pakete
              </h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Vom SEO-Audit bis zur vollständigen Suchmaschinenoptimierung
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {currentContent.packages.map((pkg, index) => (
                <Card key={index} className={`relative bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-slate-700 ${pkg.popular ? 'ring-2 ring-green-500' : ''}`}>
                  {pkg.popular && (
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-600 text-white">
                      Beliebt
                    </Badge>
                  )}
                  <CardHeader>
                    <CardTitle className="text-white text-xl">{pkg.name}</CardTitle>
                    <CardDescription className="text-3xl font-bold text-green-400">
                      {pkg.price}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {pkg.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center text-gray-300">
                          <CheckCircle className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6 bg-green-600 hover:bg-green-700">
                      SEO-Analyse starten
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-black/20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Unser SEO-Prozess
              </h2>
              <p className="text-gray-300 text-lg">
                Systematische Suchmaschinenoptimierung in 5 Phasen
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              {currentContent.process.map((step, index) => (
                <div key={index} className="flex items-start mb-8 last:mb-0">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-6">
                    {step.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-300">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white text-center mb-12">
                Häufige SEO-Fragen
              </h2>
              
              <div className="space-y-6">
                {currentContent.faq.map((item, index) => (
                  <Card key={index} className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-3">{item.question}</h3>
                      <p className="text-gray-300">{item.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-green-600 to-emerald-600">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Bereit für Top-Rankings?
              </h2>
              <p className="text-xl text-green-100 mb-8">
                Kostenloses SEO-Audit und Strategiegespräch{cityDisplayName && ` in ${cityDisplayName}`}.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-green-600 hover:bg-gray-100">
                  <Clock className="w-5 h-5 mr-2" />
                  SEO-Beratung buchen
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Search className="w-5 h-5 mr-2" />
                  SEO-Audit starten
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default SEOService;